import {
  BarChart3,
  Boxes,
  ClipboardCheck,
  DatabaseBackup,
  FileUp,
  List,
  Menu,
  Monitor,
  NotebookPen,
  Scale,
  ShieldCheck,
  SlidersHorizontal,
  Store,
  Trash2,
  Truck,
  LayoutDashboard,
  LayoutGrid,
  Receipt,
  Printer,
  ScrollText,
  Settings,
  ShoppingCart,
  Tag,
  Tags,
  UserRound,
  Wallet,
  type LucideIcon,
} from 'lucide-react'

import { api } from './api'
import { esCaja } from './config'

// El menú de navegación: catálogo de pantallas acá, personalización en la base.
//
// La división no es arbitraria. Del otro lado de cada ítem hay un componente
// compilado y una ruta del router, así que la EXISTENCIA de una pantalla no
// puede ser un dato de la base: si lo fuera, alguien podría crear un ítem que
// apunta a la nada. Lo que sí es configurable —y es lo que el local quiere
// cambiar— es el orden, el nombre visible, el icono y si el ítem se muestra:
// un local llama «Turno» a lo que otro llama «Caja chica», y el que no usa la
// caja web quiere ese ítem afuera.
//
// El servidor manda la personalización por clave; acá se resuelve contra este
// catálogo. Una clave que no está acá se IGNORA (nube más nueva que el bundle
// de una caja), y una pantalla del catálogo que el servidor no mencionó se
// muestra con sus valores de fábrica (bundle más nuevo que la nube). Las dos
// puntas pueden ir desfasadas sin dejar a nadie sin menú.

export interface Pantalla {
  /** Ruta relativa en el navegador. `null` = la pantalla no existe en la web. */
  web: string | null

  /** Ruta relativa en el ejecutable de la caja. `null` = no existe ahí. */
  caja: string | null

  /** Nombre de fábrica, por si el servidor no dice nada de esta clave. */
  titulo: string

  icono: LucideIcon

  /**
   * Qué permiso hace falta. Una LISTA significa «con alguno alcanza», que es
   * el espejo del `RequiereAlgunPermiso` del backend.
   *
   * El caso es Configuración: su pantalla tiene solapas de dos permisos
   * distintos —configurar el local y administrar usuarios— y con uno solo el
   * ítem tiene que aparecer igual, mostrando las solapas que esa persona sí
   * puede abrir. Exigir los dos dejaría la configuración fuera del alcance de
   * quien solo tiene que configurar, que es justo lo que este permiso vino a
   * separar.
   */
  permiso?: string | string[]

  /**
   * Módulo al que pertenece, si pertenece a alguno (ver lib/modulos.ts).
   *
   * Con el módulo apagado la pantalla no se muestra y su ruta cae en «no
   * encontramos esta pantalla», porque sus endpoints contestan 404: un ítem
   * que lleva a una pantalla que no puede cargar es peor que no tenerlo.
   */
  modulo?: string

  /**
   * Clave de la pantalla que la contiene, si es un submenú.
   *
   * Configuración eran ONCE solapas adentro de una sola pantalla, en dos
   * renglones de botones arriba del contenido: para ir de «Medios de pago» a
   * «Roles» había que entrar a Configuración y recién ahí elegir, y desde el
   * menú no se veía que existieran. Ahora cada una es una pantalla con su
   * dirección propia —se puede guardar en favoritos y el botón de atrás
   * funciona— y el menú las muestra como lo que son.
   *
   * El padre sigue siendo una pantalla de verdad y no una carpeta: lleva a la
   * primera que la persona puede abrir, que es lo que hace que entrar a
   * «Configuración» desde un enlace viejo siga andando.
   */
  padre?: string

  orden: number
}

// Casi todas las pantallas existen en los DOS runtimes, con direcciones
// distintas. Las que no, lo dicen en su comentario y son tres: la venta y el
// turno, que en el ejecutable son la raíz y en la web cuelgan del slug, y los
// equipos de la máquina, que en un navegador no hay.
//
// Que Gestión esté en el ejecutable es lo que evita el paso que el local venía
// haciendo a mano: el dueño que está atendiendo y quiere cargar un producto o
// cambiar un precio lo hace donde está parado, sin abrir un navegador. Los
// pedidos de esas pantallas salen a la NUBE por el puente del host local (ver
// `base` en lib/api.ts y PuenteNube en el backend), así que piden internet y
// lo dicen cuando no hay.
export const PANTALLAS: Record<string, Pantalla> = {
  // En el ejecutable el índice es la VENTA, así que el tablero tiene su propia
  // dirección: la caja se abre a la mañana y se queda ocho horas en la pantalla
  // de venta, y que «Inicio» la tapara sería cambiarle la puerta de entrada al
  // mostrador para servir a una pantalla que se mira una vez por día.
  inicio: { web: '', caja: 'inicio', titulo: 'Inicio', icono: LayoutDashboard, orden: 10 },

  // La misma pantalla con dos direcciones: en la web es /caja, en el
  // ejecutable es la raíz. Es un solo ítem del menú porque para el usuario es
  // una sola cosa.
  pos: {
    web: 'caja',
    caja: '',
    titulo: 'Caja',
    icono: ShoppingCart,
    permiso: 'pos.acceso',
    orden: 20,
  },

  turno: {
    web: 'turno',
    caja: 'turno',
    titulo: 'Turno',
    icono: Wallet,
    permiso: 'caja.abrir_turno',
    orden: 30,
  },

  // Los equipos de esta máquina. Solo en el ejecutable, y con el permiso de la
  // MÁQUINA: la balanza y la térmica son de la computadora y no del local
  // —dos cajas del mismo local pueden tener impresoras distintas—, así que no
  // se configuran desde Gestión ni las toca un cajero. Va al final, porque se
  // abre el día que se instala y casi nunca más.
  equipo: {
    web: null,
    caja: 'equipo',
    titulo: 'Balanza e impresora',
    icono: Printer,
    permiso: 'sistema.actualizar',
    orden: 90,
  },

  // Las ventas se consultan contra la NUBE, que es donde están todas: la caja
  // del mostrador solo tiene las suyas y ni siquiera las de los días que ya
  // purgó. Por eso la pantalla existe también en el ejecutable pero sus
  // pedidos van por el puente: buscar un ticket contra la base local daría una
  // respuesta incompleta sin decirlo, que es peor que no tener la pantalla.
  ventas: {
    web: 'ventas',
    caja: 'ventas',
    titulo: 'Ventas',
    icono: Receipt,
    permiso: 'reporte.ver',
    orden: 40,
  },

  // El control de caja: los turnos con su arqueo, sus movimientos y lo cobrado
  // por medio. Va al lado de las ventas y no adentro de Reportes, porque no se
  // consulta como un reporte —«cómo vengo este mes»— sino como un expediente:
  // el arqueo de ayer no cerró y hay que ir a ver ESE turno.
  //
  // Pide el permiso de reportes y, como las ventas, se consulta contra la
  // nube: son los turnos de TODAS las cajas del local. Contra la base local
  // mostraría una de las dos cajas sin decirlo, que engaña más de lo que
  // ayuda.
  turnos: {
    web: 'turnos',
    caja: 'turnos',
    titulo: 'Control de caja',
    icono: ClipboardCheck,
    permiso: 'reporte.ver',
    orden: 42,
  },

  // Los reportes los mira el dueño y no el cajero: el permiso es el que hace
  // esa diferencia, no el runtime. Los números salen de la nube, que es la que
  // tiene el costo y las ventas de todas las cajas.
  reportes: {
    web: 'reportes',
    caja: 'reportes',
    titulo: 'Reportes',
    icono: BarChart3,
    permiso: 'reporte.ver',
    orden: 45,
  },

  // El fiado se administra contra la nube. La caja SÍ puede fiar sin internet
  // —los clientes bajan por sincronización para eso— pero el saldo es la suma
  // de los movimientos de todas las cajas más los pagos del escritorio, así que
  // esta pantalla lo pide a la nube: calcularlo con lo que hay en el mostrador
  // daría un número incompleto.
  fiado: {
    web: 'fiado',
    caja: 'fiado',
    modulo: 'fiado',
    titulo: 'Fiado',
    icono: NotebookPen,
    permiso: 'fiado.ver',
    orden: 35,
  },

  // La merma se DECLARA desde el turno sin internet —la caja tiene su propio
  // camino y su cola— y esta pantalla es el REPORTE: dice qué proporción de
  // cada producto se termina tirando, y para eso hace falta el costo, que no
  // baja a la caja. Por eso va contra la nube aunque se abra desde la misma
  // máquina.
  merma: {
    web: 'merma',
    caja: 'merma',
    modulo: 'merma',
    titulo: 'Merma',
    icono: Trash2,
    permiso: 'reporte.ver',
    orden: 55,
  },

  // Las compras mueven el COSTO, que es el dato más sensible del negocio y no
  // baja a la caja: la pantalla existe en el ejecutable y pregunta a la nube,
  // así el que recibe la mercadería carga el remito donde está y no en otra
  // computadora.
  compras: {
    web: 'compras',
    caja: 'compras',
    modulo: 'compras',
    titulo: 'Compras',
    icono: Truck,
    permiso: 'compra.ver',
    orden: 58,
  },

  // El catálogo y los precios los administra la NUBE —la caja los recibe por
  // sincronización— y por eso estas dos pantallas piden internet. Son las que
  // motivaron todo esto: cambiar el precio de la banana con el local abierto
  // era irse a otra computadora.
  productos: {
    web: 'productos',
    caja: 'productos',
    titulo: 'Productos',
    icono: Boxes,
    permiso: 'producto.ver',
    orden: 50,
  },

  precios: {
    web: 'precios',
    caja: 'precios',
    titulo: 'Precios',
    icono: Tags,
    permiso: 'precio.editar',
    orden: 60,
  },

  // Las promociones van al lado de los precios y no adentro de Configuración:
  // no son un parámetro que se toca una vez, son una decisión comercial que
  // cambia con la temporada, como el precio.
  promociones: {
    web: 'promociones',
    caja: 'promociones',
    modulo: 'promociones',
    titulo: 'Promociones',
    icono: Tag,
    permiso: 'promocion.ver',
    orden: 62,
  },

  // La bitácora: quién autorizó qué. Va al lado de Configuración porque es
  // pantalla del DUEÑO —su permiso está separado de administrar a propósito— y
  // se lee contra la nube, que es donde se escriben las anotaciones de todas
  // las cajas.
  bitacora: {
    web: 'bitacora',
    caja: 'bitacora',
    titulo: 'Bitácora',
    icono: ScrollText,
    permiso: 'bitacora.ver',
    orden: 68,
  },

  // Configuración. Lo que es de ESTA máquina —la balanza y la térmica— no está
  // acá sino en «Balanza e impresora», que es la distinción de siempre: el
  // local se configura una vez para todas las cajas, y el equipamiento es de
  // cada PC.
  //
  // Sus once pantallas cuelgan de acá como submenú. Cada una declara SU
  // permiso, que es lo que hace que el encargado que solo administra usuarios
  // vea Usuarios y Roles y nada más —y al revés—, sin que la pantalla tenga que
  // esconder nada: los endpoints deciden igual.
  config: {
    web: 'config',
    caja: 'config',
    titulo: 'Configuración',
    icono: Settings,
    permiso: ['config.editar', 'usuario.editar', 'sistema.actualizar', 'sistema.backup'],
    orden: 70,
  },

  'config.local': {
    web: 'config/local', caja: 'config/local', padre: 'config',
    titulo: 'Mi local', icono: Store, permiso: 'config.editar', orden: 1,
  },
  'config.cajas': {
    web: 'config/cajas', caja: 'config/cajas', padre: 'config',
    titulo: 'Cajas', icono: Monitor, permiso: 'sistema.actualizar', orden: 2,
  },
  'config.medios': {
    web: 'config/medios', caja: 'config/medios', padre: 'config',
    titulo: 'Medios de pago', icono: Wallet, permiso: 'config.editar', orden: 3,
  },
  'config.unidades': {
    web: 'config/unidades', caja: 'config/unidades', padre: 'config',
    titulo: 'Unidades de venta', icono: Scale, permiso: 'config.editar', orden: 4,
  },
  'config.listas': {
    web: 'config/listas', caja: 'config/listas', padre: 'config',
    titulo: 'Listas', icono: List, permiso: 'config.editar', orden: 5,
  },
  'config.parametros': {
    web: 'config/parametros', caja: 'config/parametros', padre: 'config',
    titulo: 'Parámetros', icono: SlidersHorizontal, permiso: 'config.editar', orden: 6,
  },
  // La grilla del mostrador va PEGADA a Parámetros: el criterio de orden es
  // uno de ellos, y esta pantalla es donde se lo ve aplicado.
  'config.grilla': {
    web: 'config/grilla', caja: 'config/grilla', padre: 'config',
    titulo: 'Grilla de la caja', icono: LayoutGrid, permiso: 'config.editar', orden: 7,
  },
  'config.balanza': {
    web: 'config/balanza', caja: 'config/balanza', padre: 'config',
    titulo: 'Balanza', icono: Scale, permiso: 'config.editar', orden: 8,
  },
  'config.menu': {
    web: 'config/menu', caja: 'config/menu', padre: 'config',
    titulo: 'Menú', icono: Menu, permiso: 'config.editar', orden: 9,
  },

  // La importación la abre tanto el que configura el local como el que carga
  // productos: es un O y no un Y, igual que en el backend.
  'config.importar': {
    web: 'config/importar', caja: 'config/importar', padre: 'config',
    titulo: 'Importar', icono: FileUp, permiso: ['config.editar', 'producto.editar'], orden: 10,
  },

  'config.usuarios': {
    web: 'config/usuarios', caja: 'config/usuarios', padre: 'config',
    titulo: 'Usuarios', icono: UserRound, permiso: 'usuario.editar', orden: 11,
  },
  'config.roles': {
    web: 'config/roles', caja: 'config/roles', padre: 'config',
    titulo: 'Roles y permisos', icono: ShieldCheck, permiso: 'usuario.editar', orden: 12,
  },

  // El resguardo va ÚLTIMO y con su propio permiso: ese archivo es el negocio
  // entero, así que no viene de arrastre con configurar el local.
  'config.backup': {
    web: 'config/backup', caja: 'config/backup', padre: 'config',
    titulo: 'Resguardo', icono: DatabaseBackup, permiso: 'sistema.backup', orden: 13,
  },
}

/** Los iconos que el local puede elegir, por nombre. */
export const ICONOS: Record<string, LucideIcon> = {
  tablero: LayoutDashboard,
  carrito: ShoppingCart,
  billetera: Wallet,
  cajas: Boxes,
  etiquetas: Tags,
  recibo: Receipt,
  grafico: BarChart3,
  cuaderno: NotebookPen,
  tacho: Trash2,
  camion: Truck,
  engranaje: Settings,
  pergamino: ScrollText,
  planilla: ClipboardCheck,
}

export interface ItemMenuServidor {
  id: number
  clave: string
  titulo: string
  icono: string | null
  grupo: string | null
  orden: number
  visible: boolean
}

export interface ItemMenu {
  clave: string
  a: string
  titulo: string
  icono: LucideIcon
  grupo: string | null
  permiso?: string | string[]

  /** Los submenús, ya filtrados por permiso y por módulo. */
  hijos?: ItemMenu[]

  /** De quién cuelga. Lo resuelve `armarMenu`; la barra lateral no lo mira. */
  padre?: string
}

/**
 * Une el catálogo de pantallas con lo que configuró el local.
 *
 * `puede` filtra por permiso. El permiso sale del catálogo y NO de la base: si
 * fuera editable, alguien podría dejar visible un ítem que su rol no abre, y
 * el resultado sería un menú que lleva a una pantalla sin permiso.
 */
export function armarMenu(
  delServidor: ItemMenuServidor[],
  puede: (permiso: string) => boolean,

  /**
   * Qué módulos usa el local. `undefined` mientras no llegó la consulta, y ahí
   * se muestra todo: un menú que aparece vacío medio segundo en cada carga se
   * ve como un sistema roto, y el servidor decide igual.
   */
  modulos?: Record<string, boolean>,
): ItemMenu[] {
  const enCaja = esCaja()
  const porClave = new Map(delServidor.map((i) => [i.clave, i]))

  const armados: (ItemMenu & { orden: number })[] = []

  for (const [clave, pantalla] of Object.entries(PANTALLAS)) {
    const config = porClave.get(clave)

    // El servidor puede no saber de esta pantalla (bundle más nuevo que la
    // nube): se muestra con los valores de fábrica en lugar de desaparecer.
    // Lo que sí se respeta siempre es el «visible = false» explícito.
    if (config && !config.visible) continue

    const ruta = enCaja ? pantalla.caja : pantalla.web
    if (ruta === null) continue

    // El módulo apagado saca la pantalla, y no es lo mismo que el «visible =
    // false» de arriba: ese lo elige el local para no ver un ítem que sí
    // funciona, y este dice que la función no existe.
    if (pantalla.modulo && modulos?.[pantalla.modulo] === false) continue

    // Con una lista alcanza tener ALGUNO: ver el comentario del campo.
    const permisos = pantalla.permiso
      ? [pantalla.permiso].flat()
      : []

    if (permisos.length > 0 && !permisos.some((x) => puede(x))) continue

    armados.push({
      clave,
      a: ruta,
      titulo: config?.titulo?.trim() || pantalla.titulo,
      icono: (config?.icono && ICONOS[config.icono]) || pantalla.icono,
      grupo: config?.grupo ?? null,
      permiso: pantalla.permiso,
      orden: config?.orden ?? pantalla.orden,
      padre: pantalla.padre,
    })
  }

  const porOrden = (a: { orden: number; titulo: string }, b: { orden: number; titulo: string }) =>
    a.orden - b.orden || a.titulo.localeCompare(b.titulo, 'es')

  armados.sort(porOrden)

  // Los hijos se cuelgan de su padre y salen del nivel de arriba.
  //
  // Un hijo cuyo padre no quedó —porque el local lo escondió, o porque a esa
  // persona le falta el permiso del padre— se muestra igual arriba: es
  // preferible un ítem suelto a una pantalla que la persona puede abrir y que
  // el menú no nombra en ninguna parte.
  const porClaveArmada = new Map(armados.map((i) => [i.clave, i]))
  const salida: typeof armados = []

  for (const item of armados) {
    const padre = item.padre ? porClaveArmada.get(item.padre) : undefined

    if (!padre) {
      salida.push(item)
      continue
    }

    padre.hijos = [...(padre.hijos ?? []), item]
  }

  return salida
}

export const consultaMenu = {
  queryKey: ['menu'],
  queryFn: () => api.get<ItemMenuServidor[]>('/menu', { destino: 'local' }),

  // El menú cambia cuando alguien lo edita en Configuración, y eso pasa una
  // vez por año. Se cachea toda la sesión y la pantalla de configuración
  // invalida la consulta al guardar.
  staleTime: Infinity,
}
